import { useAuthStore } from "@/stores/authStore";
import { usePingStore } from "@/stores/ping-store";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ChatList } from "./ChatList";
import { ConversationView } from "./ConversationView";
import { PhoneLogin } from "./PhoneLogin";

export const HealthcareDashboard = () => {
  const { isAuthenticated } = useAuthStore();
  const { chatId } = useParams<{ chatId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedChatId, setSelectedChat } = usePingStore();
  const [activeTab, setActiveTab] = useState<"patient" | "teams">("patient");
  const currentConversationId = chatId || "";

  // Determine chat type from URL path (handle both /internal-chat and /internal-chat/:id)
  const isInternalChat = location.pathname.includes("/internal-chat");

  // Derive chatTypeState directly from URL to avoid race conditions
  const chatTypeState = isInternalChat ? "internal" : "support";

  // Sync URL params with ping store
  useEffect(() => {
    if (chatId && chatId !== selectedChatId) {
      setSelectedChat(chatId);
      return;
    }

    if (!chatId && selectedChatId) {
      setSelectedChat(null);
    }
  }, [chatId, selectedChatId, setSelectedChat]);

  // Sync activeTab with chatType from URL
  useEffect(() => {
    if (isInternalChat && activeTab !== "teams") {
      setActiveTab("teams");
    } else if (!isInternalChat && activeTab !== "patient") {
      setActiveTab("patient");
    }
  }, [isInternalChat, activeTab]);

  // Handle chat selection from ChatList
  const handleChatSelect = (newChatId: string, isInternal: boolean = false) => {
    // Navigate first - chatTypeState will be derived from URL automatically
    if (isInternal) {
      navigate(`/dashboard/internal-chat/${newChatId}`);
    } else {
      navigate(`/dashboard/chat/${newChatId}`);
    }
    // Update store after navigation
    setSelectedChat(newChatId);
  };

  const handleTabChange = (tab: "patient" | "teams") => {
    // Always navigate when switching tabs, regardless of selectedChatId
    setActiveTab(tab);

    // Clear selected chat when switching tabs
    if (selectedChatId) {
      setSelectedChat(null);
    }

    // Navigate to the appropriate base route for the tab
    if (tab === "teams") {
      navigate("/dashboard/internal-chat");
    } else {
      navigate("/dashboard/chat");
    }
  };

  if (!isAuthenticated) {
    return <PhoneLogin />;
  }

  return (
    <div className="flex h-full min-h-0 overflow-hidden p-2 md:p-3">
      <div className="flex h-full min-h-0 min-w-0 flex-1 gap-2 xl:gap-3">
        <ChatList
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          selectedConversation={currentConversationId}
          setSelectedConversation={handleChatSelect}
        />
        <ConversationView
          conversationId={currentConversationId}
          chatType={chatTypeState}
        />
      </div>
    </div>
  );
};
